import * as React from 'react'
import axios from 'axios'

// Chargebee.js Card Components inside our own layout: the card
// number / expiry / CVC live in Chargebee iframes, the app only ever sees a
// payment-intent id.
const CHARGEBEE_JS = 'https://js.chargebee.com/v2/chargebee.js'
let chargebeeJsPromise = null

export function loadChargebeeJs() {
    if (typeof window === 'undefined')
        return Promise.reject(new Error('no window'))
    if (window.Chargebee) return Promise.resolve(window.Chargebee)
    if (!chargebeeJsPromise) {
        chargebeeJsPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script')
            script.src = CHARGEBEE_JS
            script.async = true
            script.onload = () => resolve(window.Chargebee)
            script.onerror = () =>
                reject(new Error('Chargebee.js failed to load'))
            document.head.appendChild(script)
        })
    }
    return chargebeeJsPromise
}

// GET /billing/config — 404 means the embedded checkout is switched off and
// the dialog keeps its slice-3 seam.
export function useBillingConfig(open) {
    const [state, setState] = React.useState({ status: 'idle', config: null })
    React.useEffect(() => {
        if (!open || state.status !== 'idle') return
        setState({ status: 'loading', config: null })
        axios
            .get('/billing/config')
            .then(response =>
                setState({ status: 'ready', config: response.data })
            )
            .catch(error =>
                setState({
                    status:
                        error.response?.status === 404 ? 'disabled' : 'failed',
                    config: null,
                })
            )
    }, [open, state.status])
    return state
}

// Chargebee's card inputs live in its own iframes and paint their own white
// background: `backgroundColor` is not one of the style keys it honours, and
// there is no way in from outside a cross-origin frame. So the card block is
// a light surface in both themes (`.cb-field` in projectNotes.css) with dark
// text on it — the alternative, near-white text on Chargebee's white input,
// makes a typed card number invisible in dark mode.
//
// The colours have to be hex: Chargebee's style parser drops `rgb(...)`
// silently, and the fields then fall back to its defaults.
const CARD_TEXT_COLOUR = '#171717'
const CARD_PLACEHOLDER_COLOUR = '#737373'
const CARD_INVALID_COLOUR = '#dc2626'
const CARD_FIELDS = ['number', 'expiry', 'cvv']

// Chargebee.js keeps one instance per page: a second init returns the first
// one (with a console warning) rather than applying the new options.
export function chargebeeInstance(Chargebee, options) {
    try {
        return Chargebee.getInstance()
    } catch (error) {
        return Chargebee.init(options)
    }
}

// Chargebee asks VIES whether the number is registered in that country:
// 'VALID', 'INVALID' or 'UNDETERMINED' (VIES did not answer). It answers
// without a status for a country outside the EU.
export async function validateVatNumber({
    site,
    publishableKey,
    country,
    vatNumber,
}) {
    const Chargebee = await loadChargebeeJs()
    const instance = chargebeeInstance(Chargebee, { site, publishableKey })
    await instance.load('functions')
    const result = await instance.vat.validateVat({
        country,
        vat_number: vatNumber,
    })
    return (result && result.status) || 'UNDETERMINED'
}

export function useCardComponent({
    enabled,
    site,
    publishableKey,
    fieldIds,
    onComplete,
}) {
    const [state, setState] = React.useState({
        status: 'idle',
        error: null,
        complete: false,
    })
    const componentRef = React.useRef(null)
    const completeRef = React.useRef(onComplete)
    completeRef.current = onComplete

    React.useEffect(() => {
        if (!enabled || !site || !publishableKey) return undefined
        let cancelled = false
        setState({ status: 'loading', error: null, complete: false })
        loadChargebeeJs()
            .then(async Chargebee => {
                const instance = chargebeeInstance(Chargebee, {
                    site,
                    publishableKey,
                })
                await instance.load('components')
                if (cancelled) return
                const card = instance.createComponent('card', {
                    classes: {
                        focus: 'is-focus',
                        invalid: 'is-invalid',
                        empty: 'is-empty',
                        complete: 'is-complete',
                    },
                    style: {
                        base: {
                            color: CARD_TEXT_COLOUR,
                            fontSize: '15px',
                            fontFamily:
                                '-apple-system, system-ui, "Segoe UI", sans-serif',
                            '::placeholder': { color: CARD_PLACEHOLDER_COLOUR },
                        },
                        invalid: { color: CARD_INVALID_COLOUR },
                    },
                    placeholder: {
                        number: '1234 1234 1234 1234',
                        expiry: 'MM / YY',
                        cvv: 'CVC',
                    },
                })
                card.createField('number').at(`#${fieldIds.number}`)
                card.createField('expiry').at(`#${fieldIds.expiry}`)
                card.createField('cvv').at(`#${fieldIds.cvv}`)
                // Chargebee reports per field. The card is payable (which
                // enables the pay button) only when all three are complete and
                // none has an error; the error shown is the first unresolved
                // one, not whichever field changed last.
                const fieldStates = {}
                card.on('change', event => {
                    const eventError = event.error ? event.error.message : null
                    if (event.field)
                        fieldStates[event.field] = {
                            complete: Boolean(event.complete),
                            error: eventError,
                        }
                    const fields = CARD_FIELDS.map(
                        name => fieldStates[name] || {}
                    )
                    const error = event.field
                        ? fields.map(field => field.error).find(Boolean) || null
                        : eventError
                    const complete =
                        !error &&
                        (event.field
                            ? fields.every(field => field.complete)
                            : Boolean(event.complete))
                    setState(current => ({ ...current, complete, error }))
                    if (complete && completeRef.current) completeRef.current()
                })
                await card.mount()
                if (cancelled) return
                componentRef.current = card
                setState(current => ({
                    ...current,
                    status: 'ready',
                    error: null,
                }))
            })
            .catch(error => {
                if (!cancelled)
                    setState({
                        status: 'failed',
                        error: error.message,
                        complete: false,
                    })
            })
        return () => {
            cancelled = true
            componentRef.current = null
        }
    }, [
        enabled,
        site,
        publishableKey,
        fieldIds.number,
        fieldIds.expiry,
        fieldIds.cvv,
    ])

    const authorize = React.useCallback(
        (paymentIntent, billingAddress) =>
            componentRef.current.authorizeWith3ds(
                paymentIntent,
                { billingAddress },
                {
                    challenge: url =>
                        window.open(
                            url,
                            'chargebee-3ds',
                            'width=500,height=650'
                        ),
                }
            ),
        []
    )

    return { ...state, authorize }
}

export function formatMoney(amountInCents, currency) {
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency || 'EUR',
        }).format((amountInCents || 0) / 100)
    } catch (error) {
        return `${((amountInCents || 0) / 100).toFixed(2)} ${currency || ''}`
    }
}
